import {
  type FC,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import * as d3 from 'd3';
import d3Cloud from 'd3-cloud';

import type { TagWord } from 'src/types/dashboard';
import { useTagCloud } from 'src/components/dashboard/DashboardContext/TagCloudContext';
import CommentsListModal from 'src/components/dashboard/DashboardPage/components/CommentsListModal';
import {
  MAX_WORD_SIZE,
  MIN_WORD_SIZE,
} from 'src/components/dashboard/DashboardPage/widgets/TagCloudWidget/TagCloud/constants';
import {
  enlargeValue,
  getAverageZeroPoints,
} from 'src/components/dashboard/DashboardPage/widgets/TagCloudWidget/TagCloud/helper';

import { styles } from './index.styles';

export const TAG_CLOUD_TEST_ID = 'tag-cloud';

export interface TagCloudProps {
  questions: string[];
  tagCloudRef: RefObject<HTMLElement>;
  height?: number;
}

const TagCloud: FC<TagCloudProps> = ({
  questions,
  tagCloudRef,
  height = 460,
}) => {
  const { tagResults, textFilter, loading, translated } = useTagCloud();
  const { t } = useTranslation('dashboard');
  const tagCloudRefWidth = tagCloudRef.current
    ? tagCloudRef.current.clientWidth
    : 800;
  const [widgetWidth, setWidgetWidth] = useState(tagCloudRefWidth);
  const [selectedToken, setSelectedToken] = useState('');

  // reduce re-rendering by debouncing the current widget width and adding a render cooldown
  const widthDebounced = useRef(tagCloudRefWidth);
  const renderCooldown = useRef(false);
  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        widthDebounced.current = entries[0].contentRect.width;
        if (!renderCooldown.current) {
          renderCooldown.current = true;
          setTimeout(() => {
            setWidgetWidth(widthDebounced.current);
            renderCooldown.current = false;
          }, 100);
        }
      }),
    [tagResults],
  );

  useEffect(() => {
    resizeObserver.observe(tagCloudRef.current as Element);
    return () => {
      setWidgetWidth(widthDebounced.current);
      resizeObserver.disconnect();
    };
  }, []);

  const defaultFont = '"Inter", sans-serif';
  const [wordCloud, wordColorIndices] = useMemo(() => {
    let maxFrequency = 0;
    const zeroPoints = getAverageZeroPoints(tagResults);
    const sortedData: TagWord[] = tagResults
      .map(({ frequency, token, id, count }) => {
        maxFrequency = frequency > maxFrequency ? frequency : maxFrequency;
        return {
          size: enlargeValue(frequency, zeroPoints),
          text: token.toUpperCase(),
          id,
          count,
        };
      })
      .sort(({ size: f1 }, { size: f2 }) => d3.descending(f2, f1));
    maxFrequency = enlargeValue(maxFrequency, zeroPoints);

    let result: TagWord[] = [];
    if (!widgetWidth || !height) {
      // return empty words since width and height cannot be 0 or falsy to prevent errors
      return [result, []];
    }

    d3Cloud<TagWord>()
      .size([widgetWidth, height])
      .words(sortedData)
      .padding(5)
      .rotate(0)
      .fontSize(
        (d) =>
          MAX_WORD_SIZE * ((d.size as number) / maxFrequency) + MIN_WORD_SIZE,
      )
      .font(defaultFont)
      .fontWeight(700)
      .on('end', (allWords) => {
        result = allWords;
      })
      .start();

    const colorsListLength = 5;
    const colorIndices = result.map(() =>
      Math.floor(Math.random() * colorsListLength),
    );
    return [result, colorIndices];
  }, [tagResults, height, widgetWidth, translated]);

  if (loading) {
    return (
      <Box
        data-test-id={TAG_CLOUD_TEST_ID}
        sx={styles.tagCloud(widgetWidth, height)}
      >
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      data-test-id={TAG_CLOUD_TEST_ID}
      sx={styles.tagCloud(widgetWidth, height)}
    >
      {wordCloud && wordCloud.length ? (
        wordCloud.map(
          (
            {
              x = 0,
              y = 0,
              size = 0,
              weight = 0,
              text,
              font = defaultFont,
              id,
              count,
            },
            i,
          ) => (
            <Box title={String(count) || ''} key={i} sx={styles.textRoot(x, y)}>
              <Typography
                sx={styles.text(size, font, weight, wordColorIndices[i])}
                onClick={() => setSelectedToken(String(id) || '')}
              >
                {text}
              </Typography>
            </Box>
          ),
        )
      ) : (
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <Alert severity="error" icon={false} sx={styles.error}>
            {t('TagCloud.NoResults')}
          </Alert>
        </Stack>
      )}
      <CommentsListModal
        isOpen={!!selectedToken}
        question={questions?.[0]}
        onClose={() => setSelectedToken('')}
        topics={selectedToken ? [selectedToken] : []}
        initialTextFilter={textFilter}
      />
    </Box>
  );
};

export default TagCloud;
